import React, { useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Editor } from '@tinymce/tinymce-react';
import { useTheme } from '../context/ThemeContext';

const EditorWrapper = styled.div`
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;

  .tox-tinymce {
    border: none !important;
    border-radius: 0 !important;
  }
`;

const CharCount = styled.div`
  text-align: right;
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.$over ? '#ef4444' : props.theme.colors.muted};
  margin-top: 4px;
`;

const TOOLBAR_DEFAULT = 'bold italic underline strikethrough | bullist numlist | link uploadimage | blockquote | removeformat';
const TOOLBAR_COMPACT = 'bold italic | bullist numlist | link uploadimage | removeformat';

/**
 * @param {Object} props
 * @param {string} props.value - HTML content
 * @param {(html: string) => void} props.onChange
 * @param {string} [props.placeholder]
 * @param {'default'|'compact'} [props.variant]
 * @param {number} [props.height]
 * @param {number} [props.maxLength]
 * @param {boolean} [props.disabled]
 * @param {() => void} [props.onCtrlEnter]
 * @param {(file: File) => Promise<string|null>} [props.onImageUpload] - Upload image, return URL
 */
const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = '',
  variant = 'default',
  height,
  maxLength,
  disabled = false,
  onCtrlEnter,
  onImageUpload,
}) => {
  const theme = useTheme();
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const isDark = theme.mode === 'dark';

  const isCompact = variant === 'compact';
  const editorHeight = height || (isCompact ? 200 : 300);
  const toolbar = isCompact ? TOOLBAR_COMPACT : TOOLBAR_DEFAULT;

  const getTextLength = useCallback((html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html || '';
    return (tmp.textContent || tmp.innerText || '').length;
  }, []);

  const currentLength = useMemo(() => maxLength != null ? getTextLength(value) : 0, [value, maxLength, getTextLength]);

  const insertImage = useCallback(async (file) => {
    if (!file || !onImageUpload) return;
    const editor = editorRef.current;
    if (!editor) return;

    // Insert a placeholder while uploading
    const placeholderId = `img-uploading-${Date.now()}`;
    editor.insertContent(
      `<span id="${placeholderId}" style="color:${isDark ? '#60739f' : '#47556b'};font-style:italic;font-size:12px;">Uploading image\u2026</span>`
    );

    try {
      const url = await onImageUpload(file);
      if (!url) throw new Error('No URL returned');

      const placeholderEl = editor.dom.get(placeholderId);
      if (placeholderEl) {
        editor.dom.setOuterHTML(placeholderId,
          `<img src="${url}" alt="${file.name || 'image'}" style="max-width:100%; border-radius:8px;" />`
        );
      } else {
        editor.insertContent(
          `<img src="${url}" alt="${file.name || 'image'}" style="max-width:100%; border-radius:8px;" />`
        );
      }
    } catch {
      const placeholderEl = editor.dom.get(placeholderId);
      if (placeholderEl) editor.dom.remove(placeholderId);
    }
  }, [onImageUpload, isDark]);

  const handleEditorChange = useCallback((content) => {
    if (onChange) onChange(content);
  }, [onChange]);

  return (
    <div>
      <EditorWrapper theme={theme}>
        {onImageUpload && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) insertImage(file);
              e.target.value = '';
            }}
          />
        )}
        <Editor
          tinymceScriptSrc="/tinymce/tinymce.min.js"
          licenseKey="gpl"
          onInit={(evt, editor) => { editorRef.current = editor; }}
          value={value}
          onEditorChange={handleEditorChange}
          disabled={disabled}
          init={{
            base_url: '/tinymce',
            suffix: '.min',
            height: editorHeight,
            menubar: false,
            plugins: 'link lists autolink autoresize',
            toolbar: onImageUpload ? toolbar : toolbar.replace(' uploadimage |', ' |'),
            placeholder: placeholder,
            min_height: isCompact ? 150 : 200,
            max_height: isCompact ? 400 : 600,
            autoresize_bottom_margin: 8,
            convert_urls: false,
            smart_paste: false,
            skin: isDark ? 'oxide-dark' : 'oxide',
            content_css: isDark ? 'dark' : 'default',
            content_style: `
              body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                font-size: 14px;
                line-height: 1.6;
                color: ${isDark ? '#ECEDEE' : '#222939'};
                background: ${isDark ? '#161a42' : '#ffffff'};
                padding: 8px 16px !important;
                margin: 0 !important;
              }
              .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before { left: 16px !important; }
              p { margin: 4px 0; }
              img { max-width: 100%; border-radius: 8px; cursor: pointer; }
              a { color: #59caee; }
              blockquote { border-left: 3px solid ${isDark ? '#232562' : '#e2e8f0'}; padding-left: 16px; color: ${isDark ? '#60739f' : '#47556b'}; margin: 8px 0; }
              ul, ol { padding-left: 24px; }
              code { background: ${isDark ? '#0f1229' : '#f8fafc'}; padding: 2px 6px; border-radius: 4px; font-size: 0.9em; }
            `,
            branding: false,
            promotion: false,
            resize: false,
            statusbar: false,
            setup: (editor) => {
              if (onImageUpload) {
                editor.ui.registry.addButton('uploadimage', {
                  icon: 'image',
                  tooltip: 'Insert image',
                  onAction: () => fileInputRef.current?.click(),
                });

                // Intercept paste — upload images instead of embedding base64
                editor.on('paste', (e) => {
                  const items = e.clipboardData?.items;
                  if (!items) return;
                  const imageItems = Array.from(items).filter(i => i.type.startsWith('image/'));
                  if (imageItems.length === 0) return;
                  e.preventDefault();
                  for (const item of imageItems) {
                    const file = item.getAsFile();
                    if (file) insertImage(file);
                  }
                });

                // Intercept drop
                editor.on('drop', (e) => {
                  const files = e.dataTransfer?.files;
                  if (!files) return;
                  const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
                  if (imageFiles.length === 0) return;
                  e.preventDefault();
                  for (const file of imageFiles) insertImage(file);
                });
              }

              if (onCtrlEnter) {
                editor.addShortcut('ctrl+return', 'Submit', () => onCtrlEnter());
                editor.addShortcut('meta+return', 'Submit', () => onCtrlEnter());
              }
            },
          }}
        />
      </EditorWrapper>
      {maxLength != null && (
        <CharCount theme={theme} $over={currentLength > maxLength}>
          {currentLength}/{maxLength}
        </CharCount>
      )}
    </div>
  );
};

export { RichTextEditor };
